import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import api, { eventImageUrl } from "@/lib/api";
import GoldParticles from "@/components/GoldParticles";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import Countdown from "@/components/Countdown";
import { IMAGES } from "@/components/brand";

const STATS = [
  { value: "50+", label: "Prestigious Institutions" },
  { value: "10", label: "Official Events" },
  { value: "2", label: "College Levels" },
  { value: "1,200+", label: "Anticipated Delegates" },
];

const SUITS = ["♠", "♦", "♣", "♥"];

const lineAnim = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Hero() {
  const ref = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api
      .get("/hero-video")
      .then((r) => {
        if (r.data.active) setVideoUrl(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      })
      .catch(() => {});
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[94vh] items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <div className="kenburns absolute inset-0">
          <img src={IMAGES.hero} alt="Gold cards and chips on a dark table" className="h-full w-full object-cover opacity-40" />
          {videoUrl && (
            <video
              key={videoUrl}
              src={videoUrl}
              autoPlay
              muted
              loop
              playsInline
              poster={IMAGES.hero}
              data-testid="hero-bg-video"
              className="absolute inset-0 h-full w-full object-cover opacity-50"
            />
          )}
        </div>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/70 via-[#08080A]/55 to-[#08080A]" />
      <div className="absolute inset-0 vignette" />
      <div className="absolute inset-x-0 top-0 z-[6] h-10 sm:h-14 bg-black/85" data-testid="hero-letterbox-top" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 z-[6] h-10 sm:h-14 bg-gradient-to-t from-black/90 via-black/60 to-transparent" data-testid="hero-letterbox-bottom" aria-hidden="true" />
      <GoldParticles density={60} />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pt-24 pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="eyebrow mb-8"
          data-testid="hero-eyebrow"
        >
          Department of Commerce · CHRIST (Deemed to be University), Pune Lavasa
        </motion.p>

        <h1 className="font-display font-black tracking-tight leading-[0.92]">
          <span className="block overflow-hidden pb-2">
            <motion.span custom={0} variants={lineAnim} initial="hidden" animate="show" className="block text-6xl sm:text-8xl lg:text-[10rem] text-[#F6F4EE]">
              FINATEX<span className="italic text-gold-sheen">'26</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden mt-2">
            <motion.span custom={1} variants={lineAnim} initial="hidden" animate="show" className="block font-bebas text-3xl sm:text-5xl lg:text-6xl tracking-[0.18em] text-[#D4AF37]">
              ALL BETS ON YOU.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl text-sm sm:text-base leading-relaxed text-[#A19E95]"
        >
          The intercollegiate Commerce, Finance &amp; Accountancy festival where the sharpest minds in the
          country take their seat at the table. Ten events. Two days. One final table.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/register"
            data-testid="hero-register-cta"
            className="group inline-flex items-center gap-3 bg-[#D4AF37] px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#08080A] transition-all duration-300 hover:bg-[#E5C97A]"
          >
            Take Your Seat
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/events"
            data-testid="hero-events-cta"
            className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.22em] text-[#F6F4EE] transition-colors duration-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
          >
            Explore the Events
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-12"
        >
          <Countdown />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-10 flex flex-wrap gap-x-10 gap-y-3 font-mono text-xs tracking-[0.2em] text-[#B39343]"
          data-testid="hero-meta"
        >
          <span>20–21 NOVEMBER 2026</span>
          <span>LAVASA CAMPUS, PUNE</span>
          <span>SENIOR &amp; JUNIOR COLLEGE</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-[#6E6B63]"
        aria-hidden="true"
      >
        <ChevronDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function StatsStrip() {
  return (
    <section data-testid="stats-strip" className="border-y border-[#D4AF37]/10 bg-[#0B0B0F]">
      <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className={`px-6 py-10 sm:px-10 ${i !== 0 ? "border-l border-white/5" : ""}`}>
            <p className="font-display text-2xl sm:text-3xl font-bold text-[#E5C97A]">{s.value}</p>
            <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#A19E95]">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FestivalPreview() {
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-3 border border-[#D4AF37]/25" aria-hidden="true" />
            <img src={IMAGES.story} alt="Vintage casino editorial still life" className="relative aspect-[4/5] w-full object-cover" data-testid="home-story-image" />
            <span className="absolute -bottom-5 -right-3 sm:-right-5 bg-[#08080A] border border-[#D4AF37]/30 px-5 py-3 font-bebas text-xl tracking-[0.25em] text-[#D4AF37]">
              EST. LEGACY
            </span>
          </div>
        </Reveal>
        <div>
          <SectionHeading
            eyebrow="The Festival"
            title="Where Commerce Is Played at the Highest Table"
            testId="home-festival-heading"
          />
          <Reveal delay={0.1}>
            <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#A19E95]">
              FINATEX has always stood for one idea — that finance is not luck, it is nerve, preparation and
              strategy. For its 2026 edition, the Department of Commerce at CHRIST (Deemed to be University),
              Pune Lavasa raises the stakes: a two-day intercollegiate conclave dressed in the language of the
              grand casino, played entirely in the currency of intellect.
            </p>
            <p className="mt-5 text-sm sm:text-base leading-relaxed text-[#A19E95]">
              Fifty institutions. Ten official events across senior and junior college levels. The cards are
              dealt — the rest is on you.
            </p>
            <Link
              to="/about"
              data-testid="home-festival-cta"
              className="group mt-8 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#D4AF37] transition-colors hover:text-[#E5C97A]"
            >
              Read the House Story
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FeaturedEvents() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api
      .get("/events")
      .then((r) => setEvents([...r.data].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <section className="bg-[#0B0B0F] border-y border-white/5 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            eyebrow="The Events"
            title="Ten Challenges of Skill, Nerve & Numbers"
            copy="Every event is a different table — trading floors, forensic ledgers, M&A negotiations, cultural stages. Choose where you play."
            testId="home-events-heading"
          />
          <Reveal delay={0.15}>
            <Link to="/events" data-testid="home-events-all-cta" className="group mb-1 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#D4AF37] hover:text-[#E5C97A] transition-colors">
              All Ten Events
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-events-grid">
          {events.map((ev, i) => (
            <Reveal key={ev.id} delay={i * 0.08}>
              <Link
                to="/events"
                data-testid={`home-event-card-${i}`}
                className="group relative flex h-full flex-col border border-white/8 bg-[#13131A] p-7 transition-all duration-500 hover:border-[#D4AF37]/40 hover:bg-[#16161f]"
              >
                <span className="absolute right-5 top-4 text-4xl text-[#D4AF37]/15 transition-colors duration-500 group-hover:text-[#D4AF37]/35" aria-hidden="true">
                  {SUITS[i % SUITS.length]}
                </span>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B39343]">{ev.college_level}</p>
                {ev.category && (
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">{ev.category}</p>
                )}
                <h3 className="mt-4 font-display text-xl font-semibold text-[#F6F4EE]">{ev.name}</h3>
                {ev.short_description && (
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#A19E95] line-clamp-3">{ev.short_description}</p>
                )}
                {ev.prize && <p className="mt-6 font-mono text-xs tracking-[0.15em] text-[#E5C97A]">{ev.prize}</p>}
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ScheduleTeaser() {
  const [days, setDays] = useState([]);
  useEffect(() => {
    api.get("/schedule").then((r) => setDays(r.data)).catch(() => {});
  }, []);

  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
      <SectionHeading
        eyebrow="The Programme"
        title="Two Days. One Final Table."
        copy="From the opening hand on Friday morning to the prize ceremony under the lights on Saturday — every hour is accounted for."
        testId="home-schedule-heading"
      />
      <div className="mt-14 grid gap-5 lg:grid-cols-2" data-testid="home-schedule-grid">
        {days.map((day, di) => (
          <Reveal key={day.id} delay={di * 0.1}>
            <div className="h-full border border-white/8 bg-[#13131A] p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B39343]">{day.day}</p>
              <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold text-[#F6F4EE]">{day.theme}</h3>
              <div className="mt-8 flex flex-col gap-5">
                {day.slots.slice(0, 3).map((slot) => (
                  <div key={slot.time} className="flex gap-5">
                    <span className="w-20 shrink-0 font-mono text-xs text-[#E5C97A] pt-0.5">{slot.time}</span>
                    <div>
                      <p className="text-sm font-medium text-[#F6F4EE]">{slot.title}</p>
                      <p className="mt-1 text-xs text-[#6E6B63]">{slot.location}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/schedule" data-testid={`home-schedule-day-${di}-cta`} className="group mt-8 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#D4AF37] hover:text-[#E5C97A] transition-colors">
                Full Itinerary
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function VenueBanner() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid="venue-banner" className="relative overflow-hidden py-32 sm:py-44">
      <motion.div style={{ y }} className="absolute inset-0 scale-125">
        <img src={IMAGES.venue} alt="Candlelit lounge at the Lavasa campus" className="h-full w-full object-cover opacity-35" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#08080A] via-transparent to-[#08080A]" />
      <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 text-center">
        <Reveal>
          <p className="eyebrow mb-6">The Venue</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-[#F6F4EE]">
            A Lakeside City,<br />Dressed for the Occasion
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-[#A19E95]">
            Set in the hill-city of Lavasa, the campus becomes a quiet, glittering house of commerce for two
            November nights — candlelit conclaves by day, gala evenings by the water.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden border-t border-[#D4AF37]/15 bg-[#0B0B0F] py-24 sm:py-32">
      <GoldParticles density={35} />
      <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
        <Reveal>
          <span className="text-4xl text-[#D4AF37]/60" aria-hidden="true">♠</span>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-[#F6F4EE]">
            The Cards Are on the Table
          </h2>
          <p className="mx-auto mt-6 max-w-lg text-sm sm:text-base leading-relaxed text-[#A19E95]">
            Registrations are open for individual delegates and college groups. Seats at every event are
            limited — the house does not wait.
          </p>
          <Link
            to="/register"
            data-testid="home-final-cta"
            className="group mt-10 inline-flex items-center gap-3 bg-[#D4AF37] px-10 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#08080A] transition-colors duration-300 hover:bg-[#E5C97A]"
          >
            Register for FINATEX'26
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <StatsStrip />
      <FestivalPreview />
      <FeaturedEvents />
      <ScheduleTeaser />
      <VenueBanner />
      <FinalCta />
    </>
  );
}
